/**
 * 图片本地变换工具（旋转 / 翻转）
 *
 * 使用 Canvas API 对图片执行变换，返回新的 dataUrl。
 * 变换后通常创建新的图片节点并自动连线。
 */

type Degrees = 90 | 180 | 270;
type FlipDirection = 'horizontal' | 'vertical';

/**
 * 将绝对 URL 归一化为相对路径。
 * 后端存储的 resultUrl 可能含绝对地址（如 http://192.168.x.x:3001/api/...），
 * 当用户从不同主机/端口访问时，fetch 该绝对 URL 会打不到后端。
 * 剥离 host 后变为相对路径，由浏览器解析到当前页面 origin。
 */
function normalizeToRelativePath(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const parsed = new URL(url);
      // 保留 query：媒体网关 URL 的 mediaToken 在查询串里，只取 pathname 会丢令牌导致 401
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return url;
    }
  }
  return url;
}

/**
 * 加载图片为 HTMLImageElement
 *
 * 通过 fetch → blob URL 加载，避免 crossOrigin='anonymous' 在同源场景下
 * 因后端缺少 CORS 响应头导致 ERR_EMPTY_RESPONSE。
 * 同时将绝对 URL 归一化为相对路径，确保跨主机访问时仍能正确请求。
 */
export async function loadImage(src: string): Promise<HTMLImageElement> {
  let objectUrl: string | null = null;
  try {
    const normalizedSrc = normalizeToRelativePath(src);
    const response = await fetch(normalizedSrc, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = objectUrl!;
    });
  } finally {
    if (objectUrl) {
      requestAnimationFrame(() => URL.revokeObjectURL(objectUrl!));
    }
  }
}

/**
 * 旋转图片（90 / 180 / 270 度）
 */
export async function rotateImage(dataUrl: string, degrees: Degrees): Promise<string> {
  const img = await loadImage(dataUrl);
  const { naturalWidth: w, naturalHeight: h } = img;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建 Canvas 上下文');

  // 90/270 度时宽高互换
  if (degrees === 90 || degrees === 270) {
    canvas.width = h;
    canvas.height = w;
  } else {
    canvas.width = w;
    canvas.height = h;
  }

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2);
  ctx.restore();

  return canvas.toDataURL('image/png');
}

/**
 * 翻转图片（水平 / 垂直）
 */
export async function flipImage(dataUrl: string, direction: FlipDirection): Promise<string> {
  const img = await loadImage(dataUrl);
  const { naturalWidth: w, naturalHeight: h } = img;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建 Canvas 上下文');

  canvas.width = w;
  canvas.height = h;

  ctx.save();
  if (direction === 'horizontal') {
    ctx.scale(-1, 1);
    ctx.drawImage(img, -w, 0);
  } else {
    ctx.scale(1, -1);
    ctx.drawImage(img, 0, -h);
  }
  ctx.restore();

  return canvas.toDataURL('image/png');
}
